// src/components/Dialogs/OpenFormPickerDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Paper
} from '@mui/material';

// 1) Import your custom hook to fetch forms
import { useFetchAllForms } from '../../hooks/useFetchAllForms';

// 2) Import the FormCard component
import FormCard from '../Cards/FormCard';

// 3) Import the RecentTab component
import RecentTab from './RecentTab';

// 4) Import resolveScreenshotUrlsForForms
import { resolveScreenshotUrlsForForms } from '../../utils/formThumbnailResolver';

// 5) Import useAuth if you need organizationId
import { useAuth } from '../../contexts/AuthContext';

/**
 * A Google Sheets–style "Open a file" dialog for selecting a form.
 * We have three tabs: "Recent", "My Collections", "Shared with me",
 * but currently we only actually fetch data for the "Recent" tab (tabIndex=0).
 *
 * For infinite scrolling (now handled inside RecentTab):
 * - We pass forms, isLoading, hasMore, and fetchNextBatch to RecentTab.
 * - RecentTab uses an IntersectionObserver to load more forms.
 */
export default function OpenFormPickerDialog({
  open,
  onClose,
  onSelectForm // callback with the selected doc
}) {
  // 1) Tabs state
  const [tabIndex, setTabIndex] = useState(0);

  // 2) The user's selected doc
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Pull out organizationId from AuthContext (assuming you store it there)
  const { organizationId } = useAuth();

  // HOOK that fetches all forms in an "infinite scroll" approach
  // For the "Recent" tab, you might want to query forms sorted by 'publishedAt' desc,
  // but this example uses getFormsForUser as is.
  const {
    forms,
    isLoading,
    hasMore,
    fetchNextBatch
  } = useFetchAllForms(20); // 20 forms per chunk

  // 3) We add a local state to store screenshot-enriched forms
  const [enrichedForms, setEnrichedForms] = useState([]);

  // 4) Each time `forms` changes (or org changes), we enrich them with screenshot URLs
  useEffect(() => {
    if (!forms || !forms.length) {
      setEnrichedForms([]);
      return;
    }
    // If we don't have an organizationId, we can't enrich
    if (!organizationId) {
      setEnrichedForms(forms);
      return;
    }

    // Enrich the forms with screenshot URLs
    async function enrich() {
      try {
        const withScreenshots = await resolveScreenshotUrlsForForms(forms, organizationId);
        setEnrichedForms(withScreenshots);
      } catch (error) {
        console.error('[OpenFormPickerDialog] Error resolving screenshot URLs:', error);
        // Fall back to raw forms if error
        setEnrichedForms(forms);
      }
    }
    enrich();
  }, [forms, organizationId]);

  // Switch tabs => clear selection
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setSelectedDoc(null);
  };

  // Handler: user selects a doc
  const handleSelectDoc = (docObj) => {
    setSelectedDoc(docObj);
  };

  // Handler: user clicks "Select"
  const handleSelectClick = () => {
    if (onSelectForm && selectedDoc) {
      onSelectForm(selectedDoc);
    }
    onClose();
  };

  // Handler: user clicks "Cancel"
  const handleCancel = () => {
    onClose();
  };

  // We'll only display "actual" forms in the Recent tab (tabIndex === 0).
  // The other tabs will be empty for now.
  let docsToShow = [];
  if (tabIndex === 0) {
    // Instead of raw forms, we pass enrichedForms
    docsToShow = enrichedForms;
  } else if (tabIndex === 1) {
    // My Collections => temporarily empty
    docsToShow = [];
  } else {
    // Shared with me => temporarily empty
    docsToShow = [];
  }

  // Is the "Select" button disabled?
  const isSelectDisabled = !selectedDoc;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '920px',   // approximate width
          height: '500px',  // approximate height
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Single container for the title & tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          pt: 2,
          pb: 0
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            mb: 1
          }}
        >
        <Typography variant="h6" component="div">
          Open a file
        </Typography>
        </DialogTitle>

        {/* Tab bar directly below the title */}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="form picker tabs"
          sx={{ minHeight: 32 }}
        >
          <Tab label="Recent" />
          <Tab label="My Collections" />
          <Tab label="Shared with me" />
        </Tabs>
      </Box>

      {/* Content area */}
      <DialogContent
        dividers
        sx={{
          overflowY: 'auto',
          flexGrow: 1,
          mt: 2
        }}
      >
        {/* Recent tab => we've moved the infinite-scrolling logic to <RecentTab> */}
        {tabIndex === 0 && (
          <RecentTab
            forms={docsToShow}           // pass the enriched forms
            isLoading={isLoading}
            hasMore={hasMore}
            fetchNextBatch={fetchNextBatch}
            selectedDoc={selectedDoc}
            onSelectDoc={handleSelectDoc}
          />
        )}

        {/* If tabIndex===1 => My Collections => keep it empty */}
        {tabIndex === 1 && (
          <Box sx={{ p: 2 }}>
            {/* Intentionally left blank */}
          </Box>
        )}

        {/* If tabIndex===2 => Shared with me => keep it empty */}
        {tabIndex === 2 && (
          <Box sx={{ p: 2 }}>
            {/* Intentionally left blank */}
          </Box>
        )}
      </DialogContent>

      {/* Actions at the bottom */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleCancel}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelectClick}
          disabled={isSelectDisabled}
          sx={{ textTransform: 'none' }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}
